import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { isValidPhoneNumber } from 'react-phone-number-input'
import PhoneInput from 'react-phone-number-input/react-hook-form'
import { isBefore, isEqual, isValid, parse } from 'date-fns'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Trans, useTranslation } from 'react-i18next'

import { getSignedS3Url, uploadToSignedS3 } from '../../../shared/_api'
import { createAccount } from '../api/create-account'
import { TrustAccountTypes } from '../../../shared/_types'
import { dateOut } from '../../../../utils/helpers'

import Checkbox from '../../../../components/Checkbox'
import CountryInput from '../../../../components/CountryInput'
import DateInput from '../../../../components/DateInput'
import ErrorAlert from '../../../../components/ErrorAlert'
import FileUpload from '../../../../components/FileUpload'
import FormFooter from '../../../shared/FormFooter'
import InfoModal from '../../../../components/InfoModal'
import Radio from '../../../../components/Radio'
import StateDropdown from '../../../../components/StateDropdown'
import TextInput from '../../../../components/TextInput'
import TINInput from '../../../../components/TINInput'

const NewTrustAccount = () => {
  const [trust_agreement_later, setTrust_agreement_later] = useState(false)
  const [use_primary_investor_contact, setUse_primary_investor_contact] =
    useState(false)

  const nav = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const {
    control,
    watch,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<TrustAccountTypes>({
    defaultValues: {
      trust_name: '',
      title_within_trust: '',
      subtype: 'revocable',
      date_of_formation: '',
      tin: '',
      trust_agreement_uuid: '',
      phone_number: '',
      jurisdiction_of_registration: 'AL',
      use_primary_investor_contact: false,
      is_disregarded_entity: false,
      address: {
        street: '',
        street2: '',
        city: '',
        zip_code: '',
        country: 'US',
        state: 'AL',
      },
    },
  })

  const watchAddress = watch('address')

  const createAccountMutation = useMutation({
    mutationFn: (data: TrustAccountTypes) => {
      const { address, phone_number, ...rest } = data
      const payload = use_primary_investor_contact ? rest : data
      const newData = {
        ...Object.fromEntries(
          Object.entries(payload).filter(([_key, value]) => value !== '')
        ),
        date_of_formation: dateOut(data.date_of_formation),
        type: 'foe_trust' as const,
      }
      return createAccount(newData)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['ownership-types'],
      })
      nav('/dashboard/ownership-types')
    },
    onError: (error: any) => {
      console.error(error.message)
    },
  })

  const onSubmit = handleSubmit((data) => {
    createAccountMutation.mutateAsync(data)
  })

  return (
    <div className="p-6 xl:p-0">
      <div className="w-full max-w-2xl mx-auto">
        <h1 className="mb-2 text-2xl font-bold text-content-black">
          {t('common:ownershipType')}
        </h1>
        <p className="mb-6 italic text-content-black">{t('common:trust')}</p>
        <p className="mb-6 text-16 text-content-black">
          Provide the details for this trust account.
        </p>
        {Object.keys(errors).length > 0 && <ErrorAlert />}
        <form onSubmit={onSubmit}>
          <div className="mb-6">
            <div>
              <p className="mb-6 text-[19px] font-medium">
                {t('common:trust')}
              </p>
              <div className="mb-6 flex flex-col gap-6">
                <Controller
                  name="trust_name"
                  control={control}
                  rules={{ required: 'is required' }}
                  render={({ field, fieldState }) => (
                    <TextInput
                      label={t('account:nameTrust')}
                      errorMessage={fieldState.error}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="title_within_trust"
                  control={control}
                  rules={{ required: 'is required' }}
                  render={({ field, fieldState }) => (
                    <TextInput
                      label={
                        <span>
                          {t('account:titleWithinTrust')}{' '}
                          <InfoModal
                            modalContent={
                              <div className="p-8">
                                <h2 className="mb-6 text-xl font-medium text-content-black">
                                  {t('account:titleWithinTrust')}
                                </h2>
                                <p className="font-sans text-16 font-normal text-content-black">
                                  {t('account:titleWithinTrustModal')}
                                </p>
                              </div>
                            }
                          />
                        </span>
                      }
                      errorMessage={fieldState.error}
                      {...field}
                    />
                  )}
                />
                <div className="text-16 text-content-black">
                  <div className="mb-2">
                    {t('account:typeOfTrust')}{' '}
                    <InfoModal
                      modalContent={
                        <div className="p-8 text-16 font-normal text-content-black">
                          <h2 className="mb-6 text-xl font-medium text-content-black">
                            {t('account:typeOfTrust')}
                          </h2>
                          <p className="mb-6">
                            {t('account:typeOfTrustModal1')}
                          </p>
                          <p className="mb-6">
                            {t('account:typeOfTrustModal2')}
                          </p>
                          <p>
                            <Trans i18nKey="account:realEstateAdviceDisclaimer" />
                          </p>
                        </div>
                      }
                    />
                  </div>
                  <Controller
                    name="subtype"
                    control={control}
                    render={({ field, fieldState }) => (
                      <div
                        className={`flex flex-col gap-px ${
                          fieldState.error && 'border border-alert'
                        }`}
                      >
                        <Radio
                          {...field}
                          value="revocable"
                          label={t('account:revocableTrust')}
                          checked={field.value === 'revocable'}
                        />
                        <Radio
                          {...field}
                          value="irrevocable"
                          label={t('account:irrevocableTrust')}
                          checked={field.value === 'irrevocable'}
                        />
                      </div>
                    )}
                  />
                </div>
                <Controller
                  name="date_of_formation"
                  control={control}
                  rules={{
                    required: 'is required',
                    validate: {
                      isValidDate: (value) =>
                        isValid(parse(value, 'MM/dd/yyyy', new Date())) ||
                        'is not a valid date',
                      isTodayOrEarlier: (value) => {
                        const inputDate = parse(value, 'MM/dd/yyyy', new Date())
                        const today = new Date()
                        return (
                          isBefore(inputDate, today) ||
                          isEqual(inputDate, today) ||
                          'Date must not be in the future.'
                        )
                      },
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <DateInput
                      label={t('account:dateOfFormation')}
                      placeholder="mm/dd/yyyy"
                      errorMessage={fieldState.error}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="tin"
                  control={control}
                  rules={{
                    required: 'is required',
                    validate: {
                      isLongEnough: (value) => {
                        const tin = value.replace(/-/g, '')
                        return tin.length === 9 || 'is not a valid TIN'
                      },
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TINInput
                      label={
                        <span>
                          {t('account:tinLabel')}{' '}
                          <InfoModal
                            modalContent={
                              <div className="p-8">
                                <h2 className="mb-6 text-xl font-medium text-content-black">
                                  {t('account:tinLabel')}
                                </h2>
                                <p className="mb-6 font-sans text-16 font-normal text-content-black">
                                  {t('account:tinModalTrust')}
                                </p>
                              </div>
                            }
                          />
                        </span>
                      }
                      placeholder="__-_______"
                      errorMessage={fieldState.error}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="is_disregarded_entity"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Checkbox
                      label={
                        <span>
                          {t('account:isDisregardedEntity')}{' '}
                          <InfoModal
                            modalContent={
                              <div className="p-8">
                                <h2 className="mb-6 text-xl font-medium text-content-black">
                                  {t('account:disregardedEntity')}
                                </h2>
                                <p className="font-sans text-16 font-normal text-content-black">
                                  <Trans i18nKey="account:deModal" />
                                  <br />
                                  <br />
                                  <Trans i18nKey="account:realEstateAdviceDisclaimer" />
                                </p>
                              </div>
                            }
                          />
                        </span>
                      }
                      checked={field.value === true}
                      {...field}
                      errorMessage={fieldState.error}
                    />
                  )}
                />
                <Controller
                  name="jurisdiction_of_registration"
                  control={control}
                  rules={{ required: 'is required' }}
                  render={({ field, fieldState }) => (
                    <StateDropdown
                      errorMessage={fieldState.error}
                      label={t('account:jurisdictionOfRegistration')}
                      {...field}
                    />
                  )}
                />
                <Controller
                  name="use_primary_investor_contact"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Checkbox
                      {...field}
                      onChange={() => {
                        field.onChange(!field.value)
                        setUse_primary_investor_contact(
                          !use_primary_investor_contact
                        )
                      }}
                      checked={field.value === true}
                      label={t('account:trustSameAddress')}
                      errorMessage={fieldState.error}
                    />
                  )}
                />
                {!use_primary_investor_contact && (
                  <div>
                    <div className="mb-6">
                      <span
                        className={`mb-2 block text-base font-normal ${
                          errors?.phone_number?.message
                            ? 'text-alert'
                            : 'text-content-black'
                        }`}
                      >
                        {t('common:phoneNumber')}
                      </span>
                      <PhoneInput
                        name="phone_number"
                        className={`phone-input-wrapper mb-2 h-[42px] w-full rounded border ${
                          errors?.phone_number ? 'border-alert' : 'border-input'
                        } px-3 py-1.5 text-16 focus-within:outline-none focus-within:ring-1 focus-within:border-primary`}
                        defaultCountry="US"
                        countryOptionsOrder={['US']}
                        control={control}
                        initialValueFormat="national"
                        limitMaxLength={true}
                        rules={{
                          required: 'is required',
                          validate: {
                            isValidPhoneNumber: (value: string) =>
                              isValidPhoneNumber(value) ||
                              'Please enter a valid phone number.',
                          },
                        }}
                      />
                      {errors?.phone_number?.message && (
                        <p className="mb-1 text-xs text-alert">
                          {errors?.phone_number?.message}
                        </p>
                      )}
                    </div>
                    <div className="mb-6">
                      <CountryInput
                        register={register('address.country')}
                        errorMessage={errors?.address?.country?.message}
                      />
                    </div>
                    <div className="mb-2">
                      <Controller
                        name="address.street"
                        control={control}
                        rules={{ required: 'is required' }}
                        render={({ field, fieldState }) => (
                          <TextInput
                            label={t('common:address')}
                            errorMessage={fieldState.error}
                            {...field}
                          />
                        )}
                      />
                    </div>
                    <div className="mb-6">
                      <Controller
                        name="address.street2"
                        control={control}
                        render={({ field, fieldState }) => (
                          <TextInput
                            placeholder={t('common:address2')}
                            errorMessage={fieldState.error}
                            {...field}
                          />
                        )}
                      />
                    </div>
                    <div className="mb-6">
                      <Controller
                        name="address.city"
                        control={control}
                        rules={{ required: 'is required' }}
                        render={({ field, fieldState }) => (
                          <TextInput
                            label={t('common:city')}
                            errorMessage={fieldState.error}
                            {...field}
                          />
                        )}
                      />
                    </div>
                    <div className="mb-6 flex gap-3">
                      <div className="w-1/2">
                        <Controller
                          name="address.state"
                          control={control}
                          rules={{
                            required:
                              watchAddress?.country === 'US'
                                ? 'is required'
                                : false,
                          }}
                          render={({ field, fieldState }) =>
                            watchAddress?.country === 'US' ? (
                              <StateDropdown
                                errorMessage={fieldState?.error?.message}
                                label={t('common:state')}
                                {...field}
                              />
                            ) : (
                              <TextInput
                                label={
                                  watchAddress?.country === 'US'
                                    ? t('common:state')
                                    : t('common:stateProvinceRegion')
                                }
                                errorMessage={fieldState?.error?.message}
                                {...field}
                              />
                            )
                          }
                        />
                      </div>
                      <div className="w-1/2">
                        <Controller
                          name="address.zip_code"
                          control={control}
                          rules={{ required: 'is required' }}
                          render={({ field, fieldState }) => (
                            <TextInput
                              label={
                                watchAddress?.country === 'US'
                                  ? t('common:zipCode')
                                  : t('common:postalCode')
                              }
                              errorMessage={fieldState.error}
                              {...field}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {!trust_agreement_later && (
                  <>
                    <div className="mb-6 text-[19px] font-medium">
                      {t('account:trustAgreementCopy')}
                    </div>
                    <div className="mb-6">
                      <Controller
                        name="trust_agreement_uuid"
                        control={control}
                        rules={{ required: 'File is required' }}
                        render={({ field }) => (
                          <FileUpload
                            {...field}
                            accept=".pdf"
                            label={t('common:uploadDocument')}
                            uploadFileFunction={async (file: File) => {
                              const signedS3Resp = await getSignedS3Url(
                                {
                                  fileName: file.name,
                                  type: 'trust_agreement',
                                },
                                true // isDashboard = true
                              )
                              if (signedS3Resp.status === 200) {
                                const { fields, url, s3_obj_id } =
                                  signedS3Resp.data
                                const uploadResp = await uploadToSignedS3(
                                  file,
                                  url,
                                  fields
                                )
                                console.log(uploadResp)
                                setValue('trust_agreement_uuid', s3_obj_id)
                              }
                            }}
                          />
                        )}
                      />
                      {errors?.trust_agreement_uuid?.message && (
                        <span className="mb-1 text-xs text-alert">
                          {errors.trust_agreement_uuid.message}
                        </span>
                      )}
                      <p className="mt-4 text-center text-[13px]">
                        {t('common:pdfOnly')}
                      </p>
                    </div>
                  </>
                )}
                <div>
                  <Checkbox
                    name="trust_agreement_later"
                    onChange={() => {
                      setTrust_agreement_later(!trust_agreement_later)
                      setValue('trust_agreement_uuid', '')
                    }}
                    label={t('account:trustFileLater')}
                  />
                </div>
              </div>
            </div>
          </div>
          <FormFooter
            backLocation="/dashboard/ownership-types/new"
            submitFunc={onSubmit}
            disabled={createAccountMutation.isLoading}
          />
        </form>
      </div>
    </div>
  )
}

export default NewTrustAccount
